/**
 * The three air-to-air sorties this port can fly: a duel with a named ace, a
 * patrol of the lines, and a reconnaissance escort.
 *
 * Geometry convention throughout (`mission-world.ts`): the front is the plane
 * `z = 0`, home is `+z`, over the wire is `−z`, and heading 0 points at the
 * enemy. Every distance below is metres.
 *
 * **Altitudes are a port-side compromise, stated rather than hidden.**
 * `RBMB.EXE` carries 10,000 ft and 15,000 ft as its two default mission
 * altitudes. These sorties fly lower, because the AI's engagement tuning was
 * measured at ~1,200 m (docs/engineering/lessons.md, combat lane) and a
 * high-altitude pass has not been calibrated: at 15,000 ft the engine lapse
 * changes the whole character of a fight and none of the tuning holds.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import type { Vec3 } from "../flight/math3d.ts";
import type { AiSkill } from "../combat/skill.ts";
import type { Waypoint } from "../combat/route.ts";
import { buildFlight, briefingLines, aceBriefingLines, aerodromeStartLine, climbFrom, waypoint, type MissionDraw } from "./mission-build.ts";
import { missionContext, type MissionContext, type MissionOptions } from "./mission-options.ts";
import { aceById, acesFor, reconTypeFor, scoutsFor, type FamousAce } from "./mission-roster.ts";
import { assembleMission } from "./mission-assemble.ts";
import { homeAerodromeAt } from "./generators-scramble.ts";
import type { MissionBase, MissionFlight, MissionSpec } from "./mission-spec.ts";
import type { MissionObjective } from "./objectives.ts";
import { trainTraffic } from "./ground-traffic.ts";

/** Where the player's flight forms up, on their own side of the lines. */
const PLAYER_START_Z_M = 2200;
/** Radius a flight has to get inside for a waypoint objective to count as reached. */
export const OBJECTIVE_RADIUS_M = 500;

const DUEL_ALTITUDE_M = 1200;
const PATROL_ALTITUDE_M = 2000;
const ESCORT_ALTITUDE_M = 2200;

const SKILL_LADDER: readonly AiSkill[] = ["novice", "regular", "regular", "veteran"];

/** A wingman's station: off one wing, slightly back, level. */
function wingmanPosition(leader: Vec3, direction: number): Vec3 {
  return { x: leader.x + 90 * direction, y: leader.y, z: leader.z + 70 };
}

function playerFlight(context: MissionContext, position: Vec3, headingRad: number, ammunition?: "regular" | "incendiary"): MissionFlight {
  return buildFlight({
    id: "player",
    side: "friendly",
    aircraft: context.playerAircraft,
    callsign: "You",
    position,
    headingRad,
    ammunition,
  });
}

function aceOf(context: MissionContext, options: MissionOptions): FamousAce {
  const named = options.aceId ? aceById(options.aceId) : undefined;
  return named ?? context.draw.pick(acesFor(context.enemyNation));
}

/**
 * **DOGFIGHT A FAMOUS ACE.** A head-on merge with a named ace at the `ace`
 * skill tier — `PS.EXE`'s own framing, where the opponent's aeroplane is fixed
 * to the ace's historical mount. Half the draws add a wingman to each side,
 * which is the briefing template's `%a` flight-size slot doing real work.
 */
export function generateAceDuel(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "dogfight-ace");
  const ace = aceOf(context, options);
  // "The selected ace will default to his preferred aircraft ... you may
  // choose any type for the selected ace" — the manual's own example is
  // putting Richthofen in a Camel, so `enemyAircraft` covers the ace's mount
  // the same way it covers every other generator's opposition.
  const aceAircraft = options.enemyAircraft ?? ace.aircraft;
  const paired = context.draw.chance(0.5);
  const aceAltitude = DUEL_ALTITUDE_M + context.draw.int(0, 3) * 120;
  const playerPosition: Vec3 = { x: 0, y: DUEL_ALTITUDE_M, z: 1200 };
  const acePosition: Vec3 = { x: context.draw.between(-250, 250), y: aceAltitude, z: -1400 };
  const wing = context.draw.side();

  const flights: MissionFlight[] = [
    playerFlight(context, playerPosition, 0, options.ammunition),
    buildFlight({
      id: "ace",
      side: "hostile",
      aircraft: aceAircraft,
      callsign: ace.name,
      position: acePosition,
      headingRad: Math.PI,
      skill: "ace",
    }),
  ];
  if (paired) {
    flights.push(
      buildFlight({
        id: "wingman",
        side: "friendly",
        aircraft: context.playerAircraft,
        callsign: "Wingman",
        position: wingmanPosition(playerPosition, wing),
        headingRad: 0,
        skill: "regular",
        ammunition: options.ammunition,
      }),
      buildFlight({
        id: "ace-wingman",
        side: "hostile",
        aircraft: aceAircraft,
        callsign: `${ace.name}'s wingman`,
        position: wingmanPosition(acePosition, -wing),
        headingRad: Math.PI,
        skill: "veteran",
      }),
    );
  }

  return assembleMission(context, {
    catalogId: "dogfight-ace",
    type: "dogfight",
    title: paired ? `Duel: ${ace.name} (flight of two)` : `Duel: ${ace.name}`,
    flights,
    objectives: [
      {
        kind: "destroyAircraft",
        id: "kill-ace",
        label: paired ? `Shoot down ${ace.name} and his wingman` : `Shoot down ${ace.name}`,
        role: "primary",
        count: paired ? 2 : 1,
      },
    ],
    briefing: [
      ...aceBriefingLines(context.playerAircraft, aceAircraft, context.region, DUEL_ALTITUDE_M, aceAltitude, context.weather),
      ace.note,
    ],
  });
}

function enemyPatrol(
  draw: MissionDraw,
  count: number,
  anchor: Vec3,
  aircraft: AircraftKey,
): MissionFlight[] {
  const legA = waypoint({ x: anchor.x - 2600, y: anchor.y, z: anchor.z - 400 }, "LINE W");
  const legB = waypoint({ x: anchor.x + 2600, y: anchor.y, z: anchor.z + 400 }, "LINE E");
  return Array.from({ length: count }, (_unused, index) =>
    buildFlight({
      id: `hostile-${index + 1}`,
      side: "hostile",
      aircraft,
      callsign: `Enemy ${index + 1}`,
      position: { x: anchor.x + index * 110 - (count - 1) * 55, y: anchor.y + index * 40, z: anchor.z + index * 90 },
      headingRad: Math.PI / 2,
      skill: draw.pick(SKILL_LADDER),
      waypoints: [legA, legB, legA],
    }),
  );
}

/**
 * **PATROL THE FRONT.** Fly the leg, sweep back, come home. Two to four enemy
 * scouts are working the same stretch of line on a patrol of their own — they
 * are navigating, not hunting, until somebody gets inside `aiEngageRangeM`.
 */
export function generatePatrol(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "patrol");
  const { draw } = context;
  const sweep = draw.side();
  const enemyCount = draw.int(2, 4);
  const enemyAircraft = options.enemyAircraft ?? draw.pick(scoutsFor(context.enemyNation, context.era));
  const enemyAltitude = PATROL_ALTITUDE_M + draw.int(0, 4) * 100;

  const start: Vec3 = { x: 0, y: PATROL_ALTITUDE_M, z: PLAYER_START_Z_M };
  const turnPoint = waypoint({ x: sweep * 2600, y: PATROL_ALTITUDE_M, z: -1200 }, "TURN");
  const sweepBack = waypoint({ x: -sweep * 2000, y: PATROL_ALTITUDE_M, z: 400 }, "SWEEP");
  const home = waypoint({ x: 0, y: PATROL_ALTITUDE_M - 200, z: 3200 }, "HOME");
  const base = context.aerodromeStart ? homeAerodromeAt(context) : undefined;

  const player = buildFlight({
    id: "player",
    side: "friendly",
    aircraft: context.playerAircraft,
    callsign: "You",
    position: base ? base.position : start,
    headingRad: 0,
    onGround: base ? true : undefined,
    ammunition: options.ammunition,
    waypoints: base ? [climbFrom(base, PATROL_ALTITUDE_M), turnPoint, sweepBack, home] : [turnPoint, sweepBack, home],
  });

  const objectives: readonly MissionObjective[] = [
    {
      kind: "reachWaypoint",
      id: "turn-point",
      label: "Patrol out to the turn point",
      role: "primary",
      flightId: "player",
      position: turnPoint.position,
      radiusM: OBJECTIVE_RADIUS_M,
    },
    { kind: "returnToBase", id: "rtb", label: "Return across your own lines", role: "primary", flightId: "player" },
  ];

  // Enemy trains working the lines of communication behind the front. Not an
  // objective — nothing in the original scores a ground kill — but they are
  // there, they are moving, and they will burn if you go down after them.
  const trains = trainTraffic(draw, { lineZ: -2400, spreadM: 2200, side: "hostile" });

  return assembleMission(context, {
    catalogId: "patrol",
    type: "patrol",
    title: "Patrol the front",
    flights: [player, ...enemyPatrol(draw, enemyCount, { x: sweep * 2400, y: enemyAltitude, z: -1600 }, enemyAircraft)],
    objectives,
    targets: trains,
    homeBase: base,
    briefing: [
      ...(base ? [aerodromeStartLine(base)] : []),
      ...briefingLines({
        flightSize: 1,
        ownAircraft: context.playerAircraft,
        enemyCount,
        enemySkill: "regular",
        enemyNation: context.enemyNation,
        enemyLeader: "an unnamed Staffel leader",
        enemyAircraft,
        region: context.region,
        ownAltitudeM: base ? 0 : PATROL_ALTITUDE_M,
        enemyAltitudeM: enemyAltitude,
        weather: context.weather,
      }),
    ],
    portNotes: [
      "Enemy trains run the lines behind the front and can be strafed or bombed — the original's object class 5, recovered whole (docs/game/ground-traffic.md). Where their routes lie is ours, drawn from the mission seed: the original compiles routes in a shell this port does not have, and there is no railway in the terrain data for either of us to follow.",
      "No supply columns: the original's truck/tank class has a null constructor and cannot be placed even by the shipped game, and no vehicle model exists in the art at all.",
      "A ground kill is never a victory. The original's debriefing counts aircraft, balloons and Zeppelins and has no column for anything on the ground.",
    ],
  });
}

interface EscortRoster {
  readonly context: MissionContext;
  readonly recon: AircraftKey;
  readonly enemyAircraft: AircraftKey;
  readonly enemyAltitude: number;
  readonly drift: number;
  readonly legs: readonly Waypoint[];
  /** Set when `MissionOptions.aerodromeStart` asks the player's flight to launch from its own field. */
  readonly base?: MissionBase;
  readonly ammunition?: "regular" | "incendiary";
}

/** The recon flight, its escort, and the two scouts sent up to stop it. */
function escortFlights(roster: EscortRoster): MissionFlight[] {
  const { context, drift, enemyAltitude, base } = roster;
  const rendezvous: Vec3 = { x: 220, y: ESCORT_ALTITUDE_M + 150, z: 1500 };
  return [
    buildFlight({
      id: "recon",
      side: "friendly",
      aircraft: roster.recon,
      callsign: "Recon 1",
      position: { x: 0, y: ESCORT_ALTITUDE_M, z: 1200 },
      headingRad: 0,
      skill: "regular",
      waypoints: roster.legs,
      // An unarmed two-seater with a job to do does not turn and dogfight.
      routeOrders: "pressOn",
    }),
    base
      ? buildFlight({
          id: "player",
          side: "friendly",
          aircraft: context.playerAircraft,
          callsign: "You",
          position: base.position,
          headingRad: 0,
          onGround: true,
          ammunition: roster.ammunition,
          waypoints: [climbFrom(base, ESCORT_ALTITUDE_M), waypoint(rendezvous, "RENDEZVOUS")],
        })
      : playerFlight(context, rendezvous, 0, roster.ammunition),
    ...Array.from({ length: 2 }, (_unused, index) =>
      buildFlight({
        id: `hostile-${index + 1}`,
        side: "hostile",
        aircraft: roster.enemyAircraft,
        callsign: `Enemy ${index + 1}`,
        position: { x: drift + 600 - index * 240, y: enemyAltitude, z: -2600 - index * 120 },
        headingRad: Math.PI,
        skill: context.draw.pick(SKILL_LADDER),
        waypoints: [waypoint({ x: drift, y: enemyAltitude, z: 0 }, "INTERCEPT")],
      }),
    ),
  ];
}

/**
 * **ESCORT RECONNAISSANCE.** A two-seater crosses the lines to photograph, then
 * turns for home; the player's job is to still have it beside them when it
 * gets there. The recon aeroplane flies a real route through the real flight
 * model — it is slower than the scouts on both sides, and it shows.
 */
export function generateEscortRecon(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "escort-recon");
  const { draw } = context;
  const recon = reconTypeFor(context.nation, context.era);
  const enemyAircraft = options.enemyAircraft ?? draw.pick(scoutsFor(context.enemyNation, context.era));
  const enemyAltitude = ESCORT_ALTITUDE_M + 300 + draw.int(0, 3) * 120;
  const drift = draw.between(-400, 400);

  const photoPoint = waypoint({ x: drift, y: ESCORT_ALTITUDE_M, z: -1800 }, "PHOTO");
  const home = waypoint({ x: -drift * 0.5, y: ESCORT_ALTITUDE_M - 200, z: 2000 }, "HOME");
  const base = context.aerodromeStart ? homeAerodromeAt(context) : undefined;

  const flights = escortFlights({
    context,
    recon,
    enemyAircraft,
    enemyAltitude,
    drift,
    legs: [photoPoint, home],
    base,
    ammunition: options.ammunition,
  });

  const objectives: readonly MissionObjective[] = [
    {
      kind: "reachWaypoint",
      id: "photo",
      label: "See the reconnaissance flight to its objective",
      role: "primary",
      flightId: "recon",
      position: photoPoint.position,
      radiusM: OBJECTIVE_RADIUS_M,
    },
    {
      kind: "reachWaypoint",
      id: "recon-home",
      label: "See it home again",
      role: "primary",
      flightId: "recon",
      position: home.position,
      radiusM: OBJECTIVE_RADIUS_M,
    },
    { kind: "protect", id: "protect-recon", label: "Recon 1 must survive", role: "survival", flightIds: ["recon"] },
  ];

  return assembleMission(context, {
    catalogId: "escort-recon",
    type: "escortRecon",
    title: "Escort reconnaissance",
    flights,
    objectives,
    homeBase: base,
    briefing: [
      ...(base ? [aerodromeStartLine(base)] : []),
      ...briefingLines({
        flightSize: 1,
        ownAircraft: context.playerAircraft,
        enemyCount: 2,
        enemySkill: "regular",
        enemyNation: context.enemyNation,
        enemyLeader: "an unnamed Staffel leader",
        enemyAircraft,
        region: context.region,
        ownAltitudeM: base ? 0 : ESCORT_ALTITUDE_M,
        enemyAltitudeM: enemyAltitude,
        weather: context.weather,
      }),
    ],
    portNotes: [
      "The reconnaissance aeroplane carries no camera and takes no photographs: reaching its objective is the whole of the job.",
      "Its observer's gun is not modelled — a two-seater in this port is unarmed and entirely dependent on its escort.",
    ],
  });
}
